import api from "@/lib/axios";
import { queryClient } from "@/lib/queryClient";
import useAuth from "@/hooks/stores/useAuth";

export const authService = {
  async login(data) {
    // Use real API only
    const response = await api.post('/login', data);
    const { data: responseData, meta } = response.data;
    console.log('responseData', responseData)
    const user = responseData.user.name;
    console.log('user', user)
    const userId = responseData.user.id;
    const accessToken = meta.access_token;
    const refreshToken = meta.refresh_token;
    const userRole = responseData.user.roles[0].title;
    const userFlow = responseData.user.status || 'in-person'; // Get status from API response
    const paymentStatus = responseData.user.payment_status || null; // Get payment status from API response
    console.log('User role from login:', userRole);
    console.log('User flow from login:', userFlow);
    console.log('Payment status from API:', paymentStatus);
    console.log('Payment status stored (ignored for in-person):', userFlow === 'remote' ? paymentStatus : null);
    
    const { setTokens, setUserRole, setUserFlow, setPaymentStatus } = useAuth.getState();
    setTokens(accessToken, refreshToken, userId);
    setUserRole(userRole);
    setUserFlow(userFlow);
    
    // Only set payment status for remote users, ignore for in-person users
    if (userFlow === 'remote') {
      setPaymentStatus(paymentStatus);
    } else {
      setPaymentStatus(null); // Clear payment status for in-person users
    }

    // Clear cache after successful login to ensure fresh data
    queryClient.clear();

    // Force specific-slot query to refetch after login (with small delay to ensure auth state is updated)
    setTimeout(() => {
      queryClient.invalidateQueries({ 
        queryKey: ['slots', 'specific'],
        exact: false 
      });
    }, 50);

    return response.data;
  },

  async register(data) {
    // Get the current user flow from auth store to include in registration
    const { userFlow } = useAuth.getState();
    
    // Include the status field and payment_status for remote users in registration data
    const registrationData = {
      ...data,
      status: userFlow // Add status field for backend
    };
    
    // Add payment_status: pending for remote users
    if (userFlow === 'remote') {
      registrationData.payment_status = 'pending';
    }
    
    console.log('Registration data with status:', registrationData);
    
    const response = await api.post('/register', registrationData);
    console.log('Register response:', response.data);
    
    const { user, auth_token } = response.data; // Adjusted to match your API response

    // Store token properly using the same structure as login
    const { setTokens, setUserFlow, setPaymentStatus } = useAuth.getState();
    setTokens(auth_token, null, user.id); // Use auth_token as access token
    
    // Set user flow and payment status in store
    const finalUserFlow = user.status || userFlow;
    setUserFlow(finalUserFlow);
    
    // Only set payment status for remote users, ignore for in-person users
    if (finalUserFlow === 'remote') {
      setPaymentStatus(user.payment_status || null);
    } else {
      setPaymentStatus(null); // Clear payment status for in-person users
    }

    console.log('Token stored after registration:', auth_token);
    console.log('User flow set after registration:', finalUserFlow);
    console.log('Payment status from API:', user.payment_status);
    console.log('Payment status stored (ignored for in-person):', finalUserFlow === 'remote' ? (user.payment_status || null) : null);

    // Clear cache after successful registration to ensure fresh data
    queryClient.clear();

    // Force specific-slot query to refetch after registration (with small delay to ensure auth state is updated)
    setTimeout(() => {
      queryClient.invalidateQueries({ 
        queryKey: ['slots', 'specific'],
        exact: false 
      });
    }, 50);

    return response.data;
  },

  async refreshToken(refreshToken) {
    try {
      const response = await api.post("/auth/refresh-token", { refreshToken });
      return response.data;
    } catch (error) {
      throw new Error("Token refresh failed: " + error.message);
    }
  },

  async getProfile() {
    try {
      const { userId } = useAuth.getState();
      if (!userId) throw new Error("User ID not found");
      
      const response = await api.get(`/v1/get-user-data?user_id=${userId}`);
      return response.data;
    } catch (error) {
      throw new Error("Failed to fetch profile: " + error.message);
    }
  },

  async logout() {
    try {
      // First, make the API call to invalidate the session
      await api.post('/v1/logout');
      
      // Then clear local state
      const { clearTokens } = useAuth.getState();
      clearTokens();
      queryClient.clear();
      
      // Clear video URL cache
      if (window.videoUrlCache) {
        window.videoUrlCache = {};
      }
    } catch (error) {
      // Even if the API call fails, we should still clear local state
      const { clearTokens } = useAuth.getState();
      clearTokens();
      queryClient.clear();
      
      // Clear video URL cache
      if (window.videoUrlCache) {
        window.videoUrlCache = {};
      }
      throw new Error("Logout failed: " + error.message);
    }
  },

  async loginWithOtp(data) {
    try {
      const response = await api.post('/send-otp', {
        email: data.email
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async verifyOtp(data) {
    try {
      const response = await api.post('/verify-otp', {
        email: data.email,
        otp: data.otp
      });
      
      // Handle authentication similar to regular login
      const { data: responseData, meta } = response.data;
      const user = responseData.user.name;
      const userId = responseData.user.id;
      const accessToken = meta.access_token;
      const refreshToken = meta.refresh_token;
      const userRole = responseData.user.roles[0].title;
      const userFlow = responseData.user.status || 'in-person'; // Get status from API response
      const paymentStatus = responseData.user.payment_status || null; // Get payment status from API response
      
      const { setTokens, setUserRole, setUserFlow, setPaymentStatus } = useAuth.getState();
      setTokens(accessToken, refreshToken, userId);
      setUserRole(userRole);
      setUserFlow(userFlow);
      setPaymentStatus(paymentStatus);

      // Clear cache after successful OTP verification to ensure fresh data
      queryClient.clear();

      // Force specific-slot query to refetch after OTP verification (with small delay to ensure auth state is updated)
      setTimeout(() => {
        queryClient.invalidateQueries({ 
          queryKey: ['slots', 'specific'],
          exact: false 
        });
      }, 50);

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async resendOtp(email) {
    try {
      const response = await api.post('/send-otp', { email });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  requestPasswordReset(email) {
    return api.post('/v1/password/email', { email });
  },

  resetPassword(data) {
    return api.post('/v1/password/reset', data);
  },

  // Send Google OAuth token to backend
  async sendGoogleToken(googleData) {
    try {
      const response = await api.post('/v1/google-oauth/connect', {
        google_id: googleData.google_id,
        // email: googleData.email,
        access_token: googleData.access_token,
        refresh_token: googleData.refresh_token
        // expiry_time: googleData.expiry_time
      });
      
      console.log('Google OAuth token sent to backend:', response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to send Google token to backend:', error);
      throw new Error("Failed to connect Google account: " + error.message);
    }
  },

  // Complete payment for remote users
  async completePayment() {
    try {
      const response = await api.get('/v1/payment-completed');
      console.log('Payment completion response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Payment completion error:', error);
      throw error;
    }
  },

  // Check Google OAuth connection status from backend
  async checkGoogleConnection() {
    try {
      const response = await api.get('/v1/google-oauth/check-disconnect');
      console.log('Google OAuth connection status:', response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to check Google connection status:', error);
      throw new Error("Failed to check Google connection status: " + error.message);
    }
  },

  // Disconnect Google OAuth from backend
  async disconnectGoogle() {
    try {
      const response = await api.get('/v1/google-oauth/disconnect');
      console.log('Google OAuth disconnected from backend:', response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to disconnect Google from backend:', error);
      throw new Error("Failed to disconnect Google account: " + error.message);
    }
  }
};
